import {
    getItemId,
    getItemLabel,
    getItemLabelWithoutTableName,
    isCustomDimension,
    isDimension,
    isField,
    isMetric,
    isTableCalculation,
    sortTimeFrames,
    type Item,
} from '@lightdash/common';
import {
    Box,
    Group,
    Select,
    Text,
    Tooltip,
    useMantineTheme,
    type SelectProps,
} from '@mantine/core';
import {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import FieldIcon from '../Filters/FieldIcon';
import { FILTER_SELECT_LIMIT } from '../Filters/constants';

interface ItemComponentProps extends React.ComponentPropsWithoutRef<'div'> {
    item: Item;
    label: string;
    description?: string;
    size?: SelectProps['size'];
    selected?: boolean;
}

const ItemComponent = forwardRef<HTMLDivElement, ItemComponentProps>(
    ({ item, label, description, size, ...rest }, ref) => (
        <Tooltip
            disabled={!description}
            label={
                <Text truncate size={size}>
                    {description}
                </Text>
            }
            position="top"
            multiline
            maw={400}
            offset={-2}
            withinPortal
            openDelay={500}
        >
            <Box ref={ref} {...rest}>
                <Group noWrap spacing={size} maw="100%">
                    <FieldIcon
                        style={{ flexShrink: 0 }}
                        item={item}
                        selected={rest.selected}
                    />
                    <Text span size={size} style={{ wordBreak: 'normal' }}>
                        {label}
                    </Text>
                </Group>
            </Box>
        </Tooltip>
    ),
);

type FieldSelectProps<T extends Item = Item> = Omit<
    SelectProps,
    'value' | 'data' | 'onChange'
> & {
    item?: T;
    items: T[];
    inactiveItemIds?: string[];
    onChange: (value: T | undefined) => void;
    onClosed?: () => void;
    hasGrouping?: boolean;
    baseTable?: string;
    focusOnRender?: boolean;
};

const getLabel = (item: Item, hasGrouping: boolean) => {
    return hasGrouping
        ? getItemLabelWithoutTableName(item)
        : getItemLabel(item);
};

const FieldSelectComponent = <T extends Item = Item>({
    item,
    items,
    onChange,
    onClosed,
    inactiveItemIds = [],
    hasGrouping = false,
    baseTable,
    focusOnRender = false,
    ...rest
}: FieldSelectProps<T>) => {
    const theme = useMantineTheme();
    const inputRef = useRef<HTMLInputElement | null>(null); // Input ref for focus handling
    useEffect(() => {
        if (focusOnRender) {
            // focus on the input after the component has rendered by throwing it to the end of the event loop first
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 1);
        }
    }, [focusOnRender]);

    const [tableLabelMap, sortedItems] = useMemo(() => {
        const map = new Map<string, string>();

        const getTypePriority = (i: Item): number => {
            if (isDimension(i) || isCustomDimension(i)) return 1;
            if (isMetric(i)) return 2; // Additional metrics are compiled as metrics
            return 3; // Table calculations have the lowest priority
        };

        const getGroupKey = (i: Item): string => {
            if (
                isDimension(i) &&
                'timeIntervalBaseDimensionName' in i &&
                i.timeIntervalBaseDimensionName
            ) {
                return i.timeIntervalBaseDimensionName;
            }

            // TODO: We should handle group and group label ?
            return i.name;
        };

        return [
            map,
            [...items].sort((a, b) => {
                /**
                 * Sorting logic:
                 * Sorts by table first
                 * Then sorts by type
                 * 1. Dimensions & Custom dimensions (interval-type dimensions are sorted by time frame, instead of label)
                 * 2. Metrics & Additional metrics
                 * 3. Table calculations
                 * Then sorts by label
                 */
                if (
                    isField(a) &&
                    !isCustomDimension(a) &&
                    a.table &&
                    a.tableLabel
                ) {
                    map.set(a.table, a.tableLabel);
                }
                if (
                    isField(b) &&
                    !isCustomDimension(b) &&
                    b.table &&
                    b.tableLabel
                ) {
                    map.set(b.table, b.tableLabel);
                }

                // Prioritise items from the base table
                if (baseTable) {
                    const aIsInTable = 'table' in a && a.table === baseTable;
                    const bIsInTable = 'table' in b && b.table === baseTable;
                    if (aIsInTable !== bIsInTable) return aIsInTable ? -1 : 1;
                }

                // Sort by table label for items from different tables
                if (isField(a) && isField(b) && a.table !== b.table) {
                    return (a.tableLabel || '').localeCompare(
                        b.tableLabel || '',
                    );
                }

                // Sort by item type priority (dimensions + custom dimensions > metrics + custom metrics > table calculations)
                const priorityDiff = getTypePriority(a) - getTypePriority(b);
                if (priorityDiff !== 0) return priorityDiff;

                // Sort by group (timeIntervalBaseDimensionName or name)
                const groupComparison = getGroupKey(a).localeCompare(
                    getGroupKey(b),
                );
                if (groupComparison !== 0) return groupComparison;

                // Within the same group, sort time-based dimensions by their time interval
                if (
                    isDimension(a) &&
                    isDimension(b) &&
                    'timeInterval' in a &&
                    'timeInterval' in b &&
                    a.timeInterval &&
                    b.timeInterval
                ) {
                    return sortTimeFrames(a.timeInterval, b.timeInterval);
                }

                return getLabel(a, hasGrouping).localeCompare(
                    getLabel(b, hasGrouping),
                );
            }),
        ];
    }, [items, baseTable, hasGrouping]);

    const selectedItemId = useMemo(() => {
        return item ? getItemId(item) : null;
    }, [item]);

    const handleChange = useCallback(
        (value: string) => {
            const selectedField = items.find((f) => getItemId(f) === value);
            onChange(selectedField);
        },
        [items, onChange],
    );

    return (
        <Select
            limit={FILTER_SELECT_LIMIT}
            ref={inputRef}
            w="100%"
            searchable
            styles={{
                separator: {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor:
                        theme.colorScheme === 'dark'
                            ? theme.colors.dark[6]
                            : 'white',
                },
                separatorLabel: {
                    fontWeight: 600,
                },
            }}
            dropdownComponent="div"
            itemComponent={ItemComponent}
            icon={item ? <FieldIcon item={item} /> : undefined}
            placeholder={rest.placeholder ?? 'Search field...'}
            allowDeselect={false}
            {...rest}
            value={selectedItemId}
            data={sortedItems.map((i) => ({
                item: i,
                value: getItemId(i),
                label: getLabel(i, hasGrouping),
                description: isField(i) ? i.description : undefined,
                group:
                    hasGrouping && isField(i)
                        ? i.tableLabel
                        : isCustomDimension(i)
                        ? tableLabelMap.get(i.table) // Custom dimensions don't have table labels, so we use the table map to get them
                        : isTableCalculation(i)
                        ? 'Table Calculations'
                        : undefined,
                disabled: inactiveItemIds.includes(getItemId(i)),
                size: rest.size,
            }))}
            onChange={handleChange}
        />
    );
};

// Memoize with generic type support
const FieldSelect = memo(FieldSelectComponent) as typeof FieldSelectComponent;

export default FieldSelect;
